import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useSelector } from "react-redux";
import {
  selectActiveOrganizationId,
  selectIsSuperAdmin,
  selectMemberships,
} from "../../redux/authSlice";
import { loadTenantOrganizationsOnce } from "./orgNameCache";
import {
  ensureOrgListLoaded,
  getCachedOrgName,
  resolveOrgNameAsync,
  seedOrgNameMap,
  snapshotOrgNameMap,
} from "./orgNameCache";

export type OrgOption = { _id: string; name: string; slug?: string };

type SingleProps = {
  multiple?: false;
  value: string;
  onChange: (organizationId: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  /** Allow empty / placeholder */
  allowEmpty?: boolean;
  emptyLabel?: string;
};

type MultiProps = {
  multiple: true;
  value: string[];
  onChange: (organizationIds: string[]) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
};

type Props = SingleProps | MultiProps;

function normalizeOrgRow(o: any): OrgOption | null {
  const id = String(o?._id || o?.id || "").trim();
  if (!id || id === "undefined" || id === "[object Object]") return null;
  return {
    _id: id,
    name: o?.name || o?.brandName || o?.legalName || o?.slug || id,
    slug: o?.slug || "",
  };
}

function orgsFromMemberships(memberships: any[]): OrgOption[] {
  const seen = new Set<string>();
  const out: OrgOption[] = [];
  (memberships || []).forEach((m: any) => {
    const fromNested = normalizeOrgRow(m?.organization);
    const id = fromNested?._id || String(m?.organizationId || "").trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(
      fromNested || {
        _id: id,
        name: id,
        slug: "",
      }
    );
  });
  return out;
}

/**
 * Organization picker for create/edit forms.
 * Uses /tenant-organizations (already scoped by backend).
 * Falls back to Redux memberships if the API list is empty.
 */
const OrganizationSelect: React.FC<Props> = (props) => {
  const {
    label = "Organization",
    required = false,
    disabled = false,
    size = "small",
    fullWidth = true,
  } = props;
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const memberships = useSelector(selectMemberships);
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [loading, setLoading] = useState(true);
  const didDefaultMultiRef = useRef(false);

  useEffect(() => {
    didDefaultMultiRef.current = false;
  }, [activeOrganizationId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await loadTenantOrganizationsOnce();
        const ok = res?.success === 1 || res?.success === true;
        const raw = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];
        let list: OrgOption[] = ok
          ? (raw.map(normalizeOrgRow).filter(Boolean) as OrgOption[])
          : [];

        // Optional soft filter only when we have membership ids (non-super)
        if (!isSuperAdmin && (memberships || []).length) {
          const allowed = new Set(
            (memberships || [])
              .map((m: any) => String(m.organizationId || m.organization?._id || ""))
              .filter(Boolean)
          );
          if (allowed.size) {
            // Always scope to memberships — never fall back to the full tenant list
            list = list.filter((o) => allowed.has(o._id));
          }
        }

        // Fallback: build from login memberships when API returned nothing
        if (!list.length) {
          list = orgsFromMemberships(memberships || []);
        }

        if (!cancelled) setOrgs(list);
      } catch (_) {
        if (!cancelled) setOrgs(orgsFromMemberships(memberships || []));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, memberships]);

  // Auto-default single select to active org
  useEffect(() => {
    if (props.multiple) return;
    if (props.value) return;
    if (!orgs.length) return;
    const active =
      activeOrganizationId && activeOrganizationId !== "all"
        ? String(activeOrganizationId)
        : "";
    const pick =
      (active && orgs.find((o) => o._id === active)?._id) ||
      orgs.find((o) => o.slug === "yathaw")?._id ||
      orgs[0]._id;
    if (pick) props.onChange(pick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgs, activeOrganizationId]);

  // Auto-default multi select to header org once when empty (create forms)
  useEffect(() => {
    if (!props.multiple) return;
    if (didDefaultMultiRef.current) return;
    if ((props.value || []).length) {
      didDefaultMultiRef.current = true;
      return;
    }
    if (!orgs.length) return;
    const active =
      activeOrganizationId && activeOrganizationId !== "all"
        ? String(activeOrganizationId)
        : "";
    if (!active) return;
    const pick = orgs.find((o) => o._id === active)?._id;
    if (pick) {
      didDefaultMultiRef.current = true;
      props.onChange([pick]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgs, activeOrganizationId, props.multiple, props.value]);

  const nameById = useMemo(() => {
    const map: Record<string, string> = {};
    orgs.forEach((o) => {
      map[o._id] = o.name;
    });
    return map;
  }, [orgs]);

  if (props.multiple) {
    const value = props.value || [];
    return (
      <FormControl fullWidth={fullWidth} size={size} disabled={disabled || loading}>
        <InputLabel>{label}{required ? " *" : ""}</InputLabel>
        <Select
          multiple
          label={label}
          value={value}
          input={<OutlinedInput label={label} />}
          renderValue={(selected) =>
            (selected as string[])
              .map((id) => nameById[id] || id)
              .join(", ") || (loading ? "Loading…" : "Select organization")
          }
          onChange={(e: SelectChangeEvent<string[]>) => {
            const v = e.target.value;
            props.onChange(typeof v === "string" ? v.split(",") : v);
          }}
        >
          {!orgs.length && (
            <MenuItem disabled value="">
              {loading ? "Loading organizations…" : "No organizations found"}
            </MenuItem>
          )}
          {orgs.map((o) => (
            <MenuItem key={o._id} value={o._id}>
              <Checkbox checked={value.indexOf(o._id) > -1} />
              <ListItemText primary={o.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth={fullWidth} size={size} disabled={disabled || loading}>
      <InputLabel>{label}{required ? " *" : ""}</InputLabel>
      <Select
        label={label}
        value={props.value || ""}
        displayEmpty
        renderValue={(selected) => {
          const id = String(selected || "");
          if (!id) {
            return loading ? "Loading…" : "Select organization";
          }
          return nameById[id] || id;
        }}
        onChange={(e) => props.onChange(String(e.target.value))}
      >
        {(props.allowEmpty || !orgs.length) && (
          <MenuItem value="">
            <em>
              {loading
                ? "Loading organizations…"
                : props.emptyLabel ||
                  (orgs.length ? "Select organization" : "No organizations found")}
            </em>
          </MenuItem>
        )}
        {orgs.map((o) => (
          <MenuItem key={o._id} value={o._id}>
            {o.name}
            {o.slug ? ` (${o.slug})` : ""}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default OrganizationSelect;

/** Hook: orgId → name map for table columns */
export function useOrgNameMap() {
  const memberships = useSelector(selectMemberships);
  const [map, setMap] = useState<Record<string, string>>(() =>
    snapshotOrgNameMap()
  );

  useEffect(() => {
    // Seed from auth memberships immediately (even before API)
    seedOrgNameMap(
      (memberships || []).map((m: any) => ({
        _id: m?.organizationId || m?.organization?._id || m?.organization?.id,
        name:
          m?.organization?.name ||
          m?.organization?.brandName ||
          m?.organization?.legalName ||
          m?.organizationName ||
          m?.organization?.slug,
      }))
    );
    setMap(snapshotOrgNameMap());

    let cancelled = false;
    (async () => {
      await ensureOrgListLoaded();
      // Re-seed memberships in case API list was sparse
      seedOrgNameMap(
        (memberships || []).map((m: any) => ({
          _id: m?.organizationId || m?.organization?._id || m?.organization?.id,
          name:
            m?.organization?.name ||
            m?.organization?.brandName ||
            m?.organization?.legalName ||
            m?.organizationName ||
            m?.organization?.slug,
        }))
      );
      if (!cancelled) setMap(snapshotOrgNameMap());
    })();

    return () => {
      cancelled = true;
    };
  }, [memberships]);

  return map;
}

/** Resolve org display name from id (handles ObjectId-like values). */
export function resolveOrgName(
  map: Record<string, string>,
  organizationId: any
): string {
  const fromCache = getCachedOrgName(organizationId);
  if (fromCache) return fromCache;
  if (organizationId == null || organizationId === "") return "";
  if (typeof organizationId === "object") {
    if (organizationId.name || organizationId.brandName) {
      return String(organizationId.name || organizationId.brandName);
    }
    const nested =
      organizationId.$oid ||
      organizationId._id ||
      organizationId.id;
    organizationId = nested;
  }
  const key = String(organizationId || "").trim();
  if (!key || key === "all" || key === "[object Object]") return "";
  return map[key] || map[key.toLowerCase()] || "";
}

export function OrgNameCell({
  organizationId,
  map,
  fallbackLabel,
}: {
  organizationId: any;
  map?: Record<string, string>;
  fallbackLabel?: string;
}) {
  const initial =
    resolveOrgName(map || {}, organizationId) ||
    (() => {
      const fallback = String(fallbackLabel || "").trim();
      const fallbackOk =
        fallback &&
        fallback !== "—" &&
        fallback !== "undefined" &&
        fallback !== "null" &&
        !/^[a-f0-9]{24}$/i.test(fallback);
      return fallbackOk ? fallback : "";
    })();

  const [name, setName] = useState<string>(initial);

  useEffect(() => {
    let cancelled = false;
    const sync =
      resolveOrgName(map || {}, organizationId) ||
      (() => {
        const fallback = String(fallbackLabel || "").trim();
        const fallbackOk =
          fallback &&
          fallback !== "—" &&
          fallback !== "undefined" &&
          fallback !== "null" &&
          !/^[a-f0-9]{24}$/i.test(fallback);
        return fallbackOk ? fallback : "";
      })();

    if (sync) {
      setName(sync);
      return;
    }

    // ID present but name missing from map — fetch by id
    const hasId =
      organizationId != null &&
      String(
        typeof organizationId === "object"
          ? organizationId.$oid ||
              organizationId._id ||
              organizationId.id ||
              ""
          : organizationId
      ).trim() !== "";

    if (!hasId) {
      setName("");
      return;
    }

    (async () => {
      const resolved = await resolveOrgNameAsync(organizationId);
      if (!cancelled) setName(resolved || "");
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId, map, fallbackLabel]);

  return (
    <span
      className="block max-w-[160px] truncate text-sm font-medium text-[#111827]"
      title={name || undefined}
    >
      {name || "—"}
    </span>
  );
}
